import { useNavigate } from 'react-router-dom';
import DashboardCard from '@/app/fuse-layouts/layout1/components/Dashboard/DashboardCard';
import { getRoomRatioSummary } from '@/app/services/rooms/rooms';
import {
	Avatar,
	CircularProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@material-ui/core';
import Pusher from 'pusher-js';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { calculateRatio } from '../../rooms/RatioStats/helpers';
import RatioDataPopover from './RatioDataPopover';
import TableTooltip from 'shared/components/TableTooltip';

const RoomRatios = () => {
	const navigate = useNavigate();
	const [viewAll, setViewAll] = useState(false);
	const [ratios, setRatios] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const user = useSelector(({ auth }) => auth.user);

	const sortedRatios = useMemo(() => {
		return [...ratios].sort((a, b) => {
			const aStudentsPerStaffRatio = a.student_ratio_count / a.required_staff;
			const aRequiredStaff = Math.ceil(a.ratios.student_count / aStudentsPerStaffRatio);
			const aIsOutOfRatio = a.ratios.staff_count < aRequiredStaff;

			const bStudentsPerStaffRatio = b.student_ratio_count / b.required_staff;
			const bRequiredStaff = Math.ceil(b.ratios.student_count / bStudentsPerStaffRatio);
			const bIsOutOfRatio = b.ratios.staff_count < bRequiredStaff;

			if (aIsOutOfRatio && !bIsOutOfRatio) return -1;
			if (!aIsOutOfRatio && bIsOutOfRatio) return 1;
			return 0;
		});
	}, [ratios]);

	useEffect(() => {
		let isMounted = true;

		const pusher = new Pusher(import.meta.env.VITE_PUSHER_CHANNEL_ID, {
			cluster: import.meta.env.VITE_PUSHER_CLUSTER_ID,
			forceTLS: true,
		});
		const channel = pusher.subscribe(
			`ld-channel-${['school_admin', 'sub_admin'].includes(user.role[0]) ? user.data.school.id : user.school?.id}`
		);

		channel.bind('ld-room-ratio-update-event', () => {
			if (isMounted) {
				setLoading(true);
				getRoomRatioSummary()
					.then((res) => {
						if (isMounted) {
							setRatios(res.data);
							setError(null);
						}
					})
					.catch((error) => {
						if (isMounted) {
							console.error('Error fetching updated room ratios:', error);
							setError('Failed to update room ratios');
						}
					})
					.finally(() => {
						if (isMounted) {
							setLoading(false);
						}
					});
			}
		});

		return () => {
			isMounted = false;
			if (pusher.connection.state === 'connected') {
				pusher.disconnect();
			}
		};
	}, [user]);

	useEffect(() => {
		let isMounted = true;
		setLoading(true);

		const fetchRatios = async () => {
			try {
				const res = await getRoomRatioSummary();
				if (isMounted) {
					setRatios(res.data);
					setError(null);
				}
			} catch (error) {
				if (isMounted) {
					setError('Failed to load room ratios');
				}
			} finally {
				if (isMounted) {
					setLoading(false);
				}
			}
		};

		fetchRatios();

		return () => {
			isMounted = false;
		};
	}, []);

	const getMoreRooms = () => {
		setViewAll(true);
	};

	const navigateToRoom = (room) => {
		navigate(`/rooms-room/${room.id}`);
	};

	return (
		<DashboardCard
			onViewMore={getMoreRooms}
			showViewMore={!viewAll ? sortedRatios.length > 5 : null}
			title="Current Room Ratios"
		>
			<div className="rounded-8 relative" style={{ height: '422px' }}>
				{loading ? (
					<div className="flex justify-center absolute inset-0 items-center mt-16">
						<CircularProgress size={35} />
					</div>
				) : null}
				<TableContainer className="pt-0 h-full overflow-auto" component={Paper} elevation={0} id="Rooms-Listing">
					<Table className="w-full" stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell className="bg-white font-bold pl-0 sticky top-0 z-10">Room</TableCell>

								<TableCell align="center" className="bg-white font-bold pl-0 sticky top-0 z-10">
									Students/Staff Ratio
								</TableCell>
								<TableCell align="right" className="bg-white font-bold pl-0 sticky top-0 z-10">
									Status
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{!ratios[0] && !loading ? (
								<TableRow>
									<TableCell align="center" colSpan={3}>
										{error ? error : 'No rooms'}
									</TableCell>
								</TableRow>
							) : null}
							{sortedRatios.slice(0, viewAll ? sortedRatios.length : 5).map((room, key) => {
								const ratio = calculateRatio(room);
								const requiredStaff = Math.ceil((room.ratios.student_count / ratio.students) * ratio.staff);
								const isOutOfRatio = room.ratios.staff_count < requiredStaff;
								const isRoom = room?.room_type === 'room';

								return (
									<TableRow className="w-full" key={key}>
										<TableCell padding="none" style={{ padding: '16px 0px' }}>
											<div
												className="flex items-center gap-10 cursor-pointer"
												onClick={() => navigateToRoom(room)}
												onKeyDown={(e) => {
													if (e.key === 'Enter' || e.key === ' ') {
														navigateToRoom(room);
													}
												}}
												role="button"
												tabIndex={0}
											>
												<Avatar className="size-[32px]" src={room?.thumb} />
												<div className="flex ">
													{room?.name.length > 15 ? (
														<TableTooltip title={room?.name}>
															<span className="m-4 font-bold text-pd-sm hover:underline max-w-[12ch] truncate">
																{room?.name}
															</span>
														</TableTooltip>
													) : (
														<span className="m-4 font-bold text-pd-sm hover:underline max-w-[12ch]">{room?.name}</span>
													)}
												</div>
											</div>
										</TableCell>
										<TableCell align="center" colSpan={1} padding="none">
											<RatioDataPopover outOfRatio={isOutOfRatio} room={room} />
										</TableCell>

										<TableCell align="right" className="pl-0">
											{isRoom ? (
												<span className={`ml-16 ${isOutOfRatio ? 'text-red-500' : 'text-[#13A594]'}`}>
													{isOutOfRatio ? 'Out of ratio' : 'In Ratio'}
												</span>
											) : null}
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</div>
		</DashboardCard>
	);
};

export default RoomRatios;
